/**
 * Types for the raw pricing grid structure from Strapi
 */
interface RawPricingGridRow {
  id: number
  key: string
  columns: Array<{
    id: number
    key: string
    value: number
  }>
}

interface RawPricingGridItem {
  id: number
  key: string
  value: number
}

interface RawPricingGrid {
  id?: number
  siBandRows?: RawPricingGridRow[]
  roomRent?: RawPricingGridItem[]
  maternity?: RawPricingGridItem[]
  additionalLoading?: RawPricingGridItem[]
  weights?: RawPricingGridItem[]
}

/**
 * Preprocessed pricing grid structure for easy lookups
 */
export interface ProcessedPricingGrid {
  siBands: {
    [siBand: string]: {
      [coverageType: string]: number
    }
  }
  roomRent: {
    [key: string]: number
  }
  maternity: {
    [key: string]: number
  }
  additionalLoading: {
    [key: string]: number
  }
  weights: {
    [key: string]: number
  }
}

/**
 * Calculator state interface
 */
export interface CalculatorState {
  [key: string]: any
}

/**
 * Preprocesses the raw pricing grid into a lookup-friendly structure
 */
export function preprocessPricingGrid(rawGrid: RawPricingGrid | null): ProcessedPricingGrid | null {
  if (!rawGrid) return null

  const processed: ProcessedPricingGrid = {
    siBands: {},
    roomRent: {},
    maternity: {},
    additionalLoading: {},
    weights: {},
  }

  // Process SI band rows
  if (rawGrid.siBandRows) {
    rawGrid.siBandRows.forEach(row => {
      const siBand = row.key
      processed.siBands[siBand] = {}
      row.columns.forEach(column => {
        processed.siBands[siBand][column.key] = column.value
      })
    })
  }

  // Process room rent
  if (rawGrid.roomRent) {
    rawGrid.roomRent.forEach(item => {
      processed.roomRent[item.key] = item.value
    })
  }

  // Process maternity
  if (rawGrid.maternity) {
    rawGrid.maternity.forEach(item => {
      processed.maternity[item.key] = item.value
    })
  }

  // Process additional loading
  if (rawGrid.additionalLoading) {
    rawGrid.additionalLoading.forEach(item => {
      processed.additionalLoading[item.key] = item.value
    })
  }

  // Process weights
  if (rawGrid.weights) {
    rawGrid.weights.forEach(item => {
      processed.weights[item.key] = item.value
    })
  }

  return processed
}

/**
 * Calculates the premium per employee based on calculator state and processed pricing grid
 */
export function calculatePremium(state: CalculatorState, processedGrid: ProcessedPricingGrid | null): number {
  if (!processedGrid) return 0
  const noOfSpouses = Math.floor(state.employeeCount * processedGrid.weights.no_of_spouses)
  const noOfChildren = Math.floor(state.employeeCount * processedGrid.weights.no_of_children)
  const noOfParents = Math.floor(state.employeeCount * processedGrid.weights.no_of_parents)
  const noOfWomen = Math.floor(state.employeeCount * processedGrid.weights.no_of_women)
  const sumInsured = state.sumInsured
  const coverageType = state.coverageType

  let premium = 0

  // Calculating premium with room rent and maternity prices per employee for e_only, esc, escp
  if (coverageType === 'e_only') {
    const premiumE = processedGrid.siBands[sumInsured].es * processedGrid.roomRent[state.roomRent]
    let maternity = 0
    if (state.additionalCoverage && state.additionalCoverage === 'maternity') {
      maternity = (processedGrid.maternity.yes * noOfWomen) / state.employeeCount
    }
    premium = (premiumE + maternity) * state.employeeCount
  } else if (coverageType === 'esc') {
    const premiumES = processedGrid.siBands[sumInsured].es * processedGrid.roomRent[state.roomRent]
    const premiumC = processedGrid.siBands[sumInsured].c * processedGrid.roomRent[state.roomRent]
    let maternity = 0
    if (state.additionalCoverage && state.additionalCoverage === 'maternity') {
      maternity = (processedGrid.maternity.yes * noOfSpouses) / state.employeeCount
    }
    premium = (premiumES + maternity) * (state.employeeCount + noOfSpouses) + premiumC * noOfChildren
  } else if (coverageType === 'escp') {
    const premiumES = processedGrid.siBands[sumInsured].es * processedGrid.roomRent[state.roomRent]
    const premiumC = processedGrid.siBands[sumInsured].c * processedGrid.roomRent[state.roomRent]
    const premiumP = processedGrid.siBands[sumInsured].p * processedGrid.roomRent[state.roomRent]
    let maternity = 0
    if (state.additionalCoverage && state.additionalCoverage === 'maternity') {
      maternity = (processedGrid.maternity.yes * noOfSpouses) / state.employeeCount
    }
    premium =
      (premiumES + maternity) * (state.employeeCount + noOfSpouses) + premiumC * noOfChildren + premiumP * noOfParents
  }

  return premium / state.employeeCount || 0
}

export function formatCurrency(value: number): string {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(1)} Crore`
  }
  if (value >= 100000) {
    return `₹${(value / 100000).toFixed(1)} Lakh`
  }
  return `₹${Math.round(value).toLocaleString('en-IN')}`
}
